import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../../api/client";
import { PERMISSIONS } from "../../../constants/permissions";
import { usePermission } from "../../../hooks/usePermission";

// Per-part fields a P&L editor can override. An empty box means "use the
// default" — the Parts Master cost, or what workers reported.
const PART_FIELDS = [
  { key: "sellingPrice", label: "Selling Price", money: true },
  { key: "unitCost", label: "Unit Cost", money: true, defaultKey: "unitCost" },
  { key: "rejectedQty", label: "Rejected", defaultKey: "rejectedQty" },
  { key: "reworkedQty", label: "Reworked", defaultKey: "reworkedQty" },
  { key: "reworkCostPerUnit", label: "Rework Cost / Unit", money: true },
];

const MISSING_LABEL = {
  sellingPrice: "selling price",
  unitCost: "unit cost",
  reworkCostPerUnit: "rework cost",
};

const card = {
  background: "#fff",
  border: "1px solid #e4ebf5",
  borderRadius: 12,
  overflow: "hidden",
  marginBottom: 20,
};
const th = {
  padding: "10px 12px",
  textAlign: "left",
  fontWeight: 700,
  color: "#374151",
  borderBottom: "1px solid #e4ebf5",
  whiteSpace: "nowrap",
};
const td = { padding: "10px 12px", color: "#0f2d5c", verticalAlign: "top" };
const input = {
  width: 96,
  padding: "6px 8px",
  border: "1px solid #d6dfed",
  borderRadius: 6,
  fontSize: 13,
  color: "#0f2d5c",
};
const label = {
  fontSize: 11,
  fontWeight: 700,
  color: "#64748b",
  textTransform: "uppercase",
  letterSpacing: "0.4px",
};

function inr(n) {
  if (n == null) return "—";
  const sign = n < 0 ? "−" : "";
  return `${sign}₹${Math.abs(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const toField = (v) => (v == null ? "" : String(v));

// The editable copy of what the server saved, as strings for the inputs
function draftFrom(pnl) {
  return {
    otherCosts: toField(pnl.otherCosts),
    notes: pnl.notes || "",
    parts: Object.fromEntries(
      pnl.parts.map((p) => [
        p.poPartId,
        Object.fromEntries(PART_FIELDS.map((f) => [f.key, toField(p.overrides[f.key])])),
      ]),
    ),
  };
}

function Stat({ title, value, sub, tone }) {
  const color = tone === "bad" ? "#dc2626" : tone === "good" ? "#15803d" : "#0f2d5c";
  return (
    <div style={{ minWidth: 140 }}>
      <div style={label}>{title}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color, marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#64748b", marginTop: 1 }}>{sub}</div>}
    </div>
  );
}

export default function POPnlPanel({ poId, reloadKey }) {
  const canEdit = usePermission(PERMISSIONS.MANUFACTURING_PNL_MANAGE);
  const [pnl, setPnl] = useState(null);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [showStages, setShowStages] = useState(false);

  const load = useCallback(async () => {
    try {
      setError("");
      const res = await api.get(`/manufacturing/pos/${poId}/pnl`);
      setPnl(res.data.data);
      setDraft(draftFrom(res.data.data));
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || "Failed to load P&L");
    }
  }, [poId]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const dirty = useMemo(
    () => pnl && draft && JSON.stringify(draft) !== JSON.stringify(draftFrom(pnl)),
    [pnl, draft],
  );

  const setPartField = (partId, key, value) =>
    setDraft((d) => ({ ...d, parts: { ...d.parts, [partId]: { ...d.parts[partId], [key]: value } } }));

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const res = await api.put(`/manufacturing/pos/${poId}/pnl`, {
        otherCosts: draft.otherCosts,
        notes: draft.notes,
        parts: Object.entries(draft.parts).map(([poPartId, fields]) => ({ poPartId, ...fields })),
      });
      setPnl(res.data.data);
      setDraft(draftFrom(res.data.data));
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || "Failed to save P&L");
    } finally {
      setSaving(false);
    }
  };

  if (!pnl || !draft) {
    return (
      <div style={card}>
        <div style={{ padding: "16px 24px", color: error ? "#dc2626" : "#94a3b8", fontSize: 13 }}>
          {error || "Loading P&L…"}
        </div>
      </div>
    );
  }

  const { totals } = pnl;
  const missing = pnl.parts.filter((p) => p.result.missing.length > 0);
  const hasStageData = pnl.parts.some((p) => p.byStage.length > 0);

  return (
    <div style={card}>
      <div
        style={{
          padding: "16px 24px",
          borderBottom: "1px solid #f1f5f9",
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <h2 style={{ margin: 0, fontSize: 16, color: "#0f2d5c" }}>Profit &amp; Loss</h2>
        {pnl.updatedAt && (
          <span style={{ fontSize: 12, color: "#94a3b8" }}>
            Last edited {new Date(pnl.updatedAt).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
            {pnl.updatedBy ? ` by ${pnl.updatedBy}` : ""}
          </span>
        )}
        {canEdit && (
          <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
            {dirty && <span style={{ fontSize: 12, color: "#c2410c" }}>Unsaved changes</span>}
            {dirty && (
              <button
                onClick={() => setDraft(draftFrom(pnl))}
                disabled={saving}
                style={{ padding: "8px 14px", background: "#fff", color: "#0f2d5c", border: "1px solid #d6dfed", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
              >
                Discard
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={!dirty || saving}
              style={{
                padding: "8px 16px",
                background: dirty ? "#0f2d5c" : "#cbd5e1",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                cursor: dirty && !saving ? "pointer" : "default",
              }}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        )}
      </div>

      {/* Totals */}
      <div style={{ padding: "16px 24px", display: "flex", flexWrap: "wrap", gap: 28, borderBottom: "1px solid #f1f5f9" }}>
        <Stat title="Revenue" value={inr(totals.revenue)} sub="Ordered qty × selling price" />
        <Stat title="Total Cost" value={inr(totals.totalCost)} sub="Material + rework + other" />
        <Stat
          title="Quality Loss"
          value={inr(totals.qualityLoss)}
          sub={`Scrap ${inr(totals.scrapLoss)} · Rework ${inr(totals.reworkCost)}`}
          tone={totals.qualityLoss > 0 ? "bad" : undefined}
        />
        <Stat
          title={totals.profit < 0 ? "Loss" : "Profit"}
          value={inr(totals.profit)}
          sub={totals.marginPct != null ? `${totals.marginPct}% margin` : null}
          tone={totals.profit < 0 ? "bad" : totals.profit > 0 ? "good" : undefined}
        />
      </div>

      {(missing.length > 0 || pnl.unattributed.rejected > 0 || pnl.unattributed.reworked > 0) && (
        <div style={{ padding: "10px 24px", background: "#fff7ed", color: "#9a3412", fontSize: 12, borderBottom: "1px solid #fed7aa" }}>
          {missing.length > 0 && (
            <div>
              Estimate is incomplete — missing{" "}
              {missing
                .map((p) => `${p.result.missing.map((m) => MISSING_LABEL[m]).join(" and ")} for ${p.partType}`)
                .join("; ")}
              . Missing values are counted as ₹0.
            </div>
          )}
          {(pnl.unattributed.rejected > 0 || pnl.unattributed.reworked > 0) && (
            <div>
              {pnl.unattributed.rejected} rejected and {pnl.unattributed.reworked} reworked parts were reported
              before stages were recorded and could not be matched to a part. Enter them on the right part below.
            </div>
          )}
        </div>
      )}

      {/* Per-part inputs and results */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#f8fafc" }}>
              {["Part", "Qty", ...PART_FIELDS.map((f) => f.label), "Revenue", "Cost", "Quality Loss", "Profit"].map((h) => (
                <th key={h} style={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pnl.parts.map((p) => (
              <tr key={p.poPartId} style={{ borderBottom: "1px solid #f1f5f9" }}>
                <td style={{ ...td, fontWeight: 600 }}>{p.partType}</td>
                <td style={td}>{p.quantity.toLocaleString()}</td>
                {PART_FIELDS.map((f) => {
                  const fallback = f.defaultKey ? p.defaults[f.defaultKey] : null;
                  const value = draft.parts[p.poPartId]?.[f.key] ?? "";
                  const overridden = value !== "" && fallback != null && Number(value) !== fallback;
                  return (
                    <td key={f.key} style={td}>
                      {canEdit ? (
                        <input
                          type="number"
                          min="0"
                          step={f.money ? "0.01" : "1"}
                          value={value}
                          placeholder={fallback != null ? String(fallback) : f.money ? "₹" : "0"}
                          onChange={(e) => setPartField(p.poPartId, f.key, e.target.value)}
                          style={input}
                          aria-label={`${f.label} for ${p.partType}`}
                        />
                      ) : (
                        <span>
                          {f.money ? inr(p.inputs[f.key]) : (p.inputs[f.key] ?? 0).toLocaleString()}
                        </span>
                      )}
                      {f.defaultKey && fallback != null && (value === "" || overridden) && (
                        <div style={{ fontSize: 11, color: overridden ? "#c2410c" : "#94a3b8", marginTop: 3, whiteSpace: "nowrap" }}>
                          {overridden ? "Edited · " : "Using "}
                          {f.key === "unitCost" ? `Parts Master ${inr(fallback)}` : `reported ${fallback}`}
                          {overridden && canEdit && (
                            <button
                              type="button"
                              onClick={() => setPartField(p.poPartId, f.key, "")}
                              style={{ marginLeft: 4, padding: 0, border: "none", background: "none", color: "#1d4ed8", fontSize: 11, cursor: "pointer" }}
                            >
                              Reset
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  );
                })}
                <td style={td}>{inr(p.result.revenue)}</td>
                <td style={td}>{inr(p.result.totalCost)}</td>
                <td style={{ ...td, color: p.result.qualityLoss > 0 ? "#dc2626" : td.color }}>{inr(p.result.qualityLoss)}</td>
                <td style={{ ...td, fontWeight: 700, color: p.result.profit < 0 ? "#dc2626" : "#15803d" }}>
                  {inr(p.result.profit)}
                  {p.result.marginPct != null && (
                    <div style={{ fontSize: 11, fontWeight: 400, color: "#64748b" }}>{p.result.marginPct}%</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {dirty && (
        <div style={{ padding: "8px 24px", fontSize: 12, color: "#64748b" }}>
          Totals update when you save.
        </div>
      )}

      {/* PO-level costs */}
      <div style={{ padding: "16px 24px", borderTop: "1px solid #f1f5f9", display: "flex", gap: 24, flexWrap: "wrap" }}>
        <div>
          <div style={label}>Other Costs</div>
          {canEdit ? (
            <input
              type="number"
              min="0"
              step="0.01"
              value={draft.otherCosts}
              placeholder="₹0"
              onChange={(e) => setDraft((d) => ({ ...d, otherCosts: e.target.value }))}
              style={{ ...input, width: 140, marginTop: 4 }}
              aria-label="Other costs"
            />
          ) : (
            <div style={{ fontSize: 14, fontWeight: 600, color: "#0f2d5c", marginTop: 4 }}>{inr(pnl.otherCosts ?? 0)}</div>
          )}
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 3 }}>Freight, tooling, anything not per part</div>
        </div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={label}>Notes</div>
          {canEdit ? (
            <textarea
              value={draft.notes}
              maxLength={2000}
              rows={2}
              onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
              style={{ ...input, width: "100%", marginTop: 4, resize: "vertical", fontFamily: "inherit", boxSizing: "border-box" }}
              aria-label="P&L notes"
            />
          ) : (
            <div style={{ fontSize: 13, color: "#0f2d5c", marginTop: 4, whiteSpace: "pre-wrap" }}>{pnl.notes || "—"}</div>
          )}
        </div>
      </div>

      {/* Where the rejects and rework came from */}
      {hasStageData && (
        <div style={{ padding: "0 24px 16px" }}>
          <button
            type="button"
            onClick={() => setShowStages((v) => !v)}
            style={{ padding: 0, border: "none", background: "none", color: "#1d4ed8", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
          >
            {showStages ? "▾" : "▸"} Rejects &amp; rework by stage (as reported)
          </button>
          {showStages && (
            <table style={{ marginTop: 8, borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  {["Part", "Stage", "Rejected", "Reworked"].map((h) => (
                    <th key={h} style={th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pnl.parts.flatMap((p) =>
                  p.byStage.map((s) => (
                    <tr key={`${p.poPartId}-${s.stageName}`} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={td}>{p.partType}</td>
                      <td style={td}>{s.stageName}</td>
                      <td style={{ ...td, color: s.rejected > 0 ? "#dc2626" : td.color }}>{s.rejected}</td>
                      <td style={{ ...td, color: s.reworked > 0 ? "#c2410c" : td.color }}>{s.reworked}</td>
                    </tr>
                  )),
                )}
              </tbody>
            </table>
          )}
        </div>
      )}

      {error && <div style={{ padding: "0 24px 16px", color: "#dc2626", fontSize: 13 }}>{error}</div>}
    </div>
  );
}
